import { computed, onMounted, onUnmounted, ref, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';

import { useInternational } from '@/common/locale';
import { useBaseMenuStore } from '@/common/stores/base-menu';

import {
  useSharedMenuSearchState,
  useFavoriteDashboards,
  useMenuSearchContentComponent,
  useMenuSearchFilter,
  useMenuSearchNavigation,
  useMenuSearchResult,
} from './menuSearch.uses';

export const setup = () => {
  const { t } = useInternational();

  const baseMenuStore = useBaseMenuStore();
  const { isShowMenuSearch } = storeToRefs(baseMenuStore);

  const sharedState = useSharedMenuSearchState();
  const { debouncedSearchWord, groupedSearchResults } = useMenuSearchFilter(undefined, sharedState);
  const { menuSearchRecentItems } = useMenuSearchResult();
  const { favoriteDashboardList } = useFavoriteDashboards();
  const { component } = useMenuSearchContentComponent(undefined, sharedState);

  const {
    getDefaultItems,
    handleArrowUp,
    handleArrowDown,
    handleArrowDownInDefault,
    selectFirstItem,
    handleEnter,
  } = useMenuSearchNavigation(sharedState);

  const contentRef = ref<HTMLElement | null>(null);

  // Result 상태의 모든 아이템
  const resultItems = computed(() => {
    return groupedSearchResults.value.flatMap((group) => group.items);
  });

  // 첫 번째 아이템 자동 선택 (watchEffect로 통합)
  watchEffect(() => {
    const isSearchActive = !!debouncedSearchWord.value;

    if (isSearchActive) {
      // 검색 결과가 있으면 첫 번째 아이템 선택
      if (resultItems.value.length > 0) {
        selectFirstItem(resultItems.value);
      }
    } else {
      // Default 상태: Recent 또는 Dashboard 아이템이 있으면 첫 번째 선택
      const defaultItems = getDefaultItems(
        menuSearchRecentItems.value,
        favoriteDashboardList.value,
      );
      if (defaultItems.length > 0) {
        selectFirstItem(defaultItems);
      }
    }
  });

  // 키보드 이벤트 핸들러
  const handleKeyDown = (event: KeyboardEvent) => {
    const isSearchActive = !!debouncedSearchWord.value;

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const items = isSearchActive
        ? resultItems.value
        : getDefaultItems(menuSearchRecentItems.value, favoriteDashboardList.value);
      handleArrowUp(items);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (isSearchActive) {
        handleArrowDown(resultItems.value);
      } else {
        handleArrowDownInDefault(menuSearchRecentItems.value, favoriteDashboardList.value);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      // 현재 상태에 따라 모든 아이템 목록 생성
      const allItems = isSearchActive
        ? resultItems.value
        : getDefaultItems(menuSearchRecentItems.value, favoriteDashboardList.value);
      // Enter 키로 네비게이션 후 모달 닫기
      handleEnter(allItems, () => {
        isShowMenuSearch.value = false;
      });
    }
  };

  // 키보드 이벤트 리스너 등록
  onMounted(() => {
    document.addEventListener('keydown', handleKeyDown);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
  });

  return {
    t,
    component,
    contentRef,
  };
};
